import { ComponentProps, useCallback, useState } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';

import { OpDataFetchSelect } from 'new-components/DLS/OpDataFetchSelect/OpDataFetchSelect';
import { sortOptionsAlphabetically } from 'utils/sortOptionsAlphabetically';
import { OpCheckbox } from 'new-components/DLS/OpCheckbox/OpCheckbox';
import { OpTooltip } from 'new-components/DLS/OpTooltip/OpTooltip';

import './DataFetchSelectFilter.scss';

type CreateOptionLabel = NonNullable<
  ComponentProps<typeof OpDataFetchSelect>['createOptionLabel']
>;

type CreateOptionLabelProps = Parameters<CreateOptionLabel>;

type SelectFilterProps = {
  defaultValue?: string[];
  onChange: (value: string[]) => void;
  pathToTooltip?: string;
} & Pick<
  ComponentProps<typeof OpDataFetchSelect>,
  | 'queryOptions'
  | 'filterSort'
  | 'pathToLabel'
  | 'pathToValue'
  | 'createOptionLabel'
>;

// Container for the dropdown render of the antd select
const Dropdown: ComponentProps<typeof OpDataFetchSelect>['popupRender'] = (
  options,
) => {
  return <div className="data-fetch-select-filter-dropdown">{options}</div>;
};

// Container for the tag render of the antd select
const Tag: ComponentProps<typeof OpDataFetchSelect>['tagRender'] = ({
  label,
}) => {
  return <div className="data-fetch-select-filter__tag">{label}</div>;
};

/**
 * DataFetchSelectFilter
 * This filter's purpose is to gracefully handle large data sets for filterable options for the table. It allows for searching the options and pulling in more than Heliums limit as needed.
 * @param defaultValue - The default filter value.
 * @param onChange - The onChange handler for the dataFetchSelect
 * @param pathToValue - The path to the value property of the record (from the data set returned by the query options). The table component uses this value to search the data of the table.
 * @param pathToLabel - The path to the label property of the record (from the data set returned by the query options).
 * @param pathToTooltip (optional) - The path to the tooltip property of the record (from the data set returned by the query options). Used as the tooltip text on the option.
 * @param queryOptions - The queryOptions for the dataFetchSelect to retrieve the data you are filtering by.
 * @param filterSort - The sort function to sort the options - passed to dataFetchSelect
 *
 * @returns
 */
export const DataFetchSelectFilter = ({
  defaultValue,
  onChange,
  pathToValue = 'id',
  pathToLabel = 'name',
  pathToTooltip,
  queryOptions,
  createOptionLabel: createOptionLabelProp,
  filterSort = sortOptionsAlphabetically,
}: SelectFilterProps) => {
  const { t } = useTranslation();
  const [selectValue, setSelectValue] = useState<string[]>(defaultValue || []);

  const handleChange = useCallback(
    (value: string[]) => {
      setSelectValue(value);
      onChange(value);
    },
    [onChange],
  );

  // Need to use createOptionLabel vs OptionRender so that the antd select onclick event fires when clicking anywhere in the option including the checkbox
  const createOptionLabel = useCallback(
    (record: CreateOptionLabelProps[0]) => {
      const label = createOptionLabelProp
        ? createOptionLabelProp(record)
        : get(record, pathToLabel);

      const optionValue = get(record, pathToValue);
      const tooltip = pathToTooltip ? get(record, pathToTooltip) : '';

      const isSelected = selectValue
        .map((val) => String(val))
        .includes(String(optionValue));

      return (
        <div className="data-fetch-select-filter-dropdown__option">
          <OpTooltip title={tooltip}>
            <OpCheckbox
              className="data-fetch-select-filter-dropdown__checkbox"
              checked={isSelected}
            >
              {label}
            </OpCheckbox>
          </OpTooltip>
        </div>
      );
    },
    [
      createOptionLabelProp,
      pathToLabel,
      pathToTooltip,
      pathToValue,
      selectValue,
    ],
  );

  return (
    <OpDataFetchSelect
      className="data-fetch-select-filter"
      testId="table-filter-data-fetch-select"
      createOptionLabel={createOptionLabel}
      fetchAllInitialValues
      placeholder={t('Filter')}
      pathToValue={pathToValue}
      pathToLabel={pathToLabel}
      onChange={handleChange}
      maxTagCount={0}
      maxTagPlaceholder={t('{{selectedCount}} selected', {
        selectedCount: selectValue.length,
      })}
      mode="multiple"
      value={selectValue}
      allowClear
      queryOptions={queryOptions}
      filterSort={filterSort}
      tagRender={Tag}
      popupRender={Dropdown}
      styles={{
        popup: {
          root: {
            width: 'fit-content',
          },
        },
      }}
    />
  );
};
